import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Spin, Typography } from 'antd'

const { Paragraph } = Typography

const FINIX_JS_V2 = 'https://js.finix.com/v/2/finix.js'

export type FinixCheckoutConfig = {
  applicationId: string | null
  merchantId: string
  merchantIdentityId: string | null
  environment: 'sandbox' | 'live'
  amount: number
  currency: string
  applePayEnabled?: boolean
  googlePayEnabled?: boolean
  achEnabled?: boolean
  paymentMethods?: Array<'card' | 'bank'>
}

export interface FinixPayCheckoutProps {
  checkoutToken: string
  secretToken: string
  finix: FinixCheckoutConfig
  amountLabel?: string
  /** Kept for call-site compatibility; validation is done via onValidate */
  apiBaseUrl?: string
  pdfToken?: string
  /** Override form methods; defaults to finix.paymentMethods or ['card'] */
  paymentMethods?: Array<'card' | 'bank'>
  onValidate: (payload: {
    rawDataResponse: string
    checkoutToken: string
    secretToken: string
  }) => Promise<void>
  onSuccess: () => void
  onError: (message: string) => void
}

declare global {
  interface Window {
    Finix?: {
      PaymentForm?: (
        element: string | HTMLElement,
        environment: string,
        applicationId: string,
        options?: Record<string, unknown>
      ) => unknown
    }
  }
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve()
      return
    }
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)))
      return
    }
    const s = document.createElement('script')
    s.id = id
    s.src = src
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.body.appendChild(s)
  })
}

function finixJsEnvironment(envName: 'sandbox' | 'live'): string {
  return envName === 'live' ? 'prod' : 'sandbox'
}

/**
 * Public / portal Finix card or bank checkout using Finix.js v2 PaymentForm.
 */
export const FinixPayCheckout: React.FC<FinixPayCheckoutProps> = ({
  checkoutToken,
  secretToken,
  finix,
  amountLabel,
  paymentMethods: paymentMethodsProp,
  onValidate,
  onSuccess,
  onError,
}) => {
  const [formReady, setFormReady] = useState(false)
  const [validating, setValidating] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const formMountedRef = useRef(false)
  const onValidateRef = useRef(onValidate)
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onValidateRef.current = onValidate
    onSuccessRef.current = onSuccess
    onErrorRef.current = onError
  }, [onValidate, onSuccess, onError])

  const methods = useMemo((): Array<'card' | 'bank'> => {
    if (paymentMethodsProp?.length) return paymentMethodsProp
    if (finix.paymentMethods?.length) return finix.paymentMethods
    return ['card']
  }, [finix.paymentMethods, paymentMethodsProp])

  const formMountId = useMemo(
    () =>
      `finix-public-form-${checkoutToken.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 24)}-${methods.join('-')}`,
    [checkoutToken, methods]
  )

  const bankOnly = methods.length === 1 && methods[0] === 'bank'
  const submitLabel = bankOnly ? 'Pay with bank' : 'Pay with card'

  useEffect(() => {
    formMountedRef.current = false
    setFormReady(false)
    setFormError(null)

    const applicationId = String(finix.applicationId || '').trim()
    if (!applicationId) {
      const msg =
        'Finix Application ID is missing. Ensure FINIX_APPLICATION_ID is configured for this environment.'
      setFormError(msg)
      onErrorRef.current(msg)
      return
    }

    let cancelled = false

    ;(async () => {
      try {
        await new Promise<void>((r) => requestAnimationFrame(() => r()))
        if (cancelled) return

        await loadScript(FINIX_JS_V2, 'finix-js-v2')
        if (cancelled) return

        if (!window.Finix?.PaymentForm) {
          throw new Error('Finix.js PaymentForm is unavailable')
        }
        if (!document.getElementById(formMountId)) {
          throw new Error('Finix form container was not found')
        }

        formMountedRef.current = true
        window.Finix.PaymentForm(
          formMountId,
          finixJsEnvironment(finix.environment),
          applicationId,
          {
            paymentMethods: methods,
            showAddress: true,
            submitLabel,
            styles: {
              default: {
                color: '#111827',
                border: '1px solid #d1d5db',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '16px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                backgroundColor: '#ffffff',
              },
            },
            onLoad: () => {
              if (!cancelled) setFormReady(true)
            },
            onSubmit: async (error: Error | null, response: { data?: { id?: string } } | null) => {
              if (error) {
                onErrorRef.current(error.message || 'Tokenization failed')
                return
              }
              const token = response?.data?.id
              if (!token) {
                onErrorRef.current('Finix did not return a payment token')
                return
              }

              setValidating(true)
              try {
                await onValidateRef.current({
                  rawDataResponse: JSON.stringify({ token }),
                  checkoutToken,
                  secretToken,
                })
                onSuccessRef.current()
              } catch (e) {
                onErrorRef.current(e instanceof Error ? e.message : 'Payment validation failed')
              } finally {
                setValidating(false)
              }
            },
          }
        )
        window.setTimeout(() => {
          if (!cancelled) setFormReady(true)
        }, 2000)
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Failed to load Finix form'
          setFormError(msg)
          onErrorRef.current(msg)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [checkoutToken, finix.applicationId, finix.environment, formMountId, methods, secretToken, submitLabel])

  return (
    <div>
      {amountLabel && (
        <Alert message={amountLabel} type="info" showIcon style={{ marginBottom: 16 }} />
      )}
      {formError && <Alert type="error" showIcon message={formError} style={{ marginBottom: 12 }} />}
      {!formReady && !formError && (
        <div style={{ textAlign: 'center', padding: 24, marginBottom: 12 }}>
          <Spin />
          <Alert
            type="info"
            showIcon
            message={bankOnly ? 'Loading secure bank form…' : 'Loading secure card form…'}
            style={{ marginTop: 16 }}
          />
        </div>
      )}
      <div id={formMountId} style={{ minHeight: 180 }} />
      {validating && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin /> Confirming payment…
        </div>
      )}
      <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
        Secure checkout powered by Finix
        {bankOnly ? ' (ACH may take 1–3 business days to clear)' : ''}
      </Paragraph>
    </div>
  )
}

export default FinixPayCheckout
